import { MoreVert, Add, Delete } from '@mui/icons-material';
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteMaterialDialog from '../DeleteMaterialDialog';
import { BillableMaterial } from '../types';

interface BillableMaterialItemProps {
  material: BillableMaterial;
  isSelected: boolean;
  isDirty?: boolean;
  onSelect: (bm: BillableMaterial) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function MaterialsOverviewItem({
  material,
  isSelected,
  isDirty,
  onSelect,
  onDuplicate,
  onDelete,
}: BillableMaterialItemProps) {
  const { t } = useTranslation();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const numberOfConfiguredRules = useMemo(() => {
    return material?.conditionSets?.filter((cs) => cs.conditions.length > 0).length || 0;
  }, [material?.conditionSets]);
  const handleSelectMaterial = () => {
    onSelect(material);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleSelectDelete = () => {
    setIsDeleteDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleDelete = () => {
    onDelete(material.id);
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
        boxSizing: 'border-box',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        height: '6em',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flex: 1,
          minWidth: 0,
          height: '100%',
          padding: '1em',
          boxSizing: 'border-box',
        }}
        onClick={handleSelectMaterial}
      >
        {material.name ? (
          <Typography
            variant='h6'
            sx={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              fontStyle: isDirty ? 'italic' : 'normal',
            }}
          >
            {material.name}
            {isDirty && '*'}
          </Typography>
        ) : (
          <Typography
            variant='h6'
            sx={{
              color: 'darkgrey',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              fontStyle: isDirty ? 'italic' : 'normal',
            }}
          >
            ({t('billableMaterial.missingName')}){isDirty && '*'}
          </Typography>
        )}

        <Typography variant='body2'>{t('billableMaterial.rule', { count: numberOfConfiguredRules })}</Typography>
      </Box>
      <IconButton onClick={handleOpenMenu}>
        <MoreVert />
      </IconButton>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={() => onDuplicate(material.id)}>
          <ListItemIcon>
            <Add />
          </ListItemIcon>
          <ListItemText>{t('common.duplicate')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSelectDelete}>
          <ListItemIcon>
            <Delete fontSize='small' />
          </ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <DeleteMaterialDialog
        isOpen={isDeleteDialogOpen}
        onDelete={handleDelete}
        onCancel={handleDeleteCancel}
        materialName={material.name}
      />
    </Box>
  );
}
